import { getSubjectLabels } from '@/lib/settings';
import React from 'react';
import { Link } from 'react-router-dom';
import {
  Home,
  Info,
  Menu,
  PanelBottom,
  Palette,
  Images,
  MapPin,
  Megaphone,
  Music,
  CreditCard,
  FileStack,
  Users,
  Scale,
  Cookie,
  HelpCircle,
  Pin,
  Briefcase,
} from 'lucide-react';
import { Card, CardContent } from '../../../../components/ui/card';

interface CmsLink {
  to: string;
  icon: React.ReactNode;
  title: string;
  description: string;
}

const GROUPS: { heading: string; items: CmsLink[] }[] = [
  {
    heading: 'Globale Inhalte',
    items: [
      { to: '/system/cms/home', icon: <Home className="h-5 w-5" />, title: 'Startseite', description: 'Hero, Philosophie, FamilyCard, Statistiken, Franchise' },
      { to: '/system/cms/ueber-uns', icon: <Info className="h-5 w-5" />, title: 'Über uns', description: 'Geschichte, Gründer, Team, Programmpunkte' },
      { to: '/system/cms/navigation', icon: <Menu className="h-5 w-5" />, title: 'Navigation', description: 'Marke, Menülinks, CTA' },
      { to: '/system/cms/footer', icon: <PanelBottom className="h-5 w-5" />, title: 'Footer', description: 'Beschreibung, Links, Kontakt, Rechtliches' },
      { to: '/system/cms/seiten', icon: <FileStack className="h-5 w-5" />, title: 'Seiten', description: 'Eigene Seiten aus Blöcken (/seite/…)' },
      { to: '/system/cms/landingpages', icon: <Megaphone className="h-5 w-5" />, title: 'Landingpages', description: 'Kampagnenseiten je Fach und Standort (/lp/…)' },
      { to: '/system/cms/faq', icon: <HelpCircle className="h-5 w-5" />, title: 'FAQ', description: 'Zentrale FAQs, pro Landingpage ein-/ausblendbar' },
      { to: '/system/cms/jobs', icon: <Briefcase className="h-5 w-5" />, title: 'Jobs-Seite', description: 'Hero der Übersicht /jobs; Stellen kommen aus den Standorten' },
      { to: '/system/cms/familycard', icon: <CreditCard className="h-5 w-5" />, title: 'FamilyCard-Seite', description: 'Hero und Vorteile der Übersicht /familycard' },
      { to: '/system/cms/news', icon: <Pin className="h-5 w-5" />, title: 'News', description: 'Neuigkeiten/Pinnwand, mehreren Standorten zuweisbar' },
    ],
  },
  {
    heading: 'Standortbezogen',
    items: [
      { to: '/system/cms/standorte', icon: <MapPin className="h-5 w-5" />, title: 'Standorte', description: 'Übersicht-Hero, Inhalte, Galerie, Team, SEO' },
      { to: '/system/cms/faecher', icon: <Music className="h-5 w-5" />, title: 'Fächer', description: 'Übersicht-Hero, Beschreibungen, Bilder, SEO' },
      { to: '/system/cms/partners', icon: <CreditCard className="h-5 w-5" />, title: 'FamilyCard', description: 'Partner zentral verwalten und zuordnen' },
      { to: '/system/cms/team', icon: <Users className="h-5 w-5" />, title: 'Team', description: 'Teammitglieder zentral verwalten und Standorten zuordnen' },
    ],
  },
  {
    heading: 'Rechtliches',
    items: [
      { to: '/system/cms/rechtstexte', icon: <Scale className="h-5 w-5" />, title: 'Rechtstexte', description: 'Impressum, Datenschutzerklärung, AGB' },
      { to: '/system/cms/cookie', icon: <Cookie className="h-5 w-5" />, title: 'Cookie-Banner', description: 'Text und Kategorien des Consent-Banners' },
    ],
  },
  {
    heading: 'Darstellung & Medien',
    items: [
      { to: '/system/cms/branding', icon: <Palette className="h-5 w-5" />, title: 'Branding', description: 'Logo, Farben, Firmendaten' },
      { to: '/system/cms/media', icon: <Images className="h-5 w-5" />, title: 'Medienbibliothek', description: 'Bilder und Videos hochladen und verwalten' },
    ],
  },
];

const CmsOverview: React.FC = () => (
  <div className="mx-auto max-w-5xl space-y-8 p-4 sm:p-6">
    <div>
      <h1 className="text-2xl font-semibold">CMS</h1>
      <p className="text-sm text-muted-foreground">
        Inhalte, Bilder und SEO der Website zentral verwalten. Das Design bleibt unverändert –
        bearbeitet werden nur Texte und Bilder.
      </p>
    </div>

    {GROUPS.map((group) => (
      <section key={group.heading} className="space-y-3">
        <h2 className="text-sm font-medium uppercase tracking-wide text-muted-foreground">
          {group.heading}
        </h2>
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
          {group.items.map((item) => (
            <Link key={item.to} to={item.to} className="block">
              <Card interactive className="h-full transition hover:border-primary">
                <CardContent className="flex items-start gap-3 p-4">
                  <span className="mt-0.5 text-primary">{item.icon}</span>
                  <div>
                    <div className="font-medium">{item.to === '/system/cms/faecher' ? getSubjectLabels().plural : item.title}</div>
                    <p className="text-sm text-muted-foreground">{item.description}</p>
                  </div>
                </CardContent>
              </Card>
            </Link>
          ))}
        </div>
      </section>
    ))}
  </div>
);

export default CmsOverview;
